import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { CmpMemberDashboardAccessReturn, useGetActiveCategoryAdvertisingBannersForChamberMemberQuery } from '../../../graphql/types';
import { RedirectUri } from '../../enumStrings';
import MemberInvoiceNavSummary from '../../pages/ChamberMemberAdmin/Invoices/MemberInvoiceNavSummary';
import CmpMemberHelpModal from './CmpMemberHelpModal';
import { getExternalLinkUrl } from '../utilities/externalLinkUrl';
import PerksHandIcon from '../../../images/nav-icons/cmp-perks-hand.png';
import HelpIcon from '../../../images/nav-icons/cmp-help-gold.png';
import LogoutIcon from '../../../images/nav-icons/cmp-logout-red.png';
import CmpSwitchChamber from './CmpSwitchChamber';

// Wave C ACON-1 / Member Dashboard design (CMP Accelerator.dc.html <aside>,
// lines 37-121): the CMP member sidebar. CPA-only members keep the original
// navigation (Nav.tsx).
const GOLD = '#E1A731';
const GRAY = '#727272';
const GREEN = '#10B981';
const BLACK = '#000000';
const DIVIDER = '#ECECEE';
const RED = '#ED1C24';

export type CmpMemberSidebarProps = {
  access: CmpMemberDashboardAccessReturn;
  chamberName?: string | null;
  memberName?: string | null;
  memberLogo?: string | null;
  featuredMember: boolean;
  sponsorLogo?: string | null;
  sponsorLogoLink?: string | null;
  /** Essentials (CPA Basic) chambers do not offer perks (design: locked items). */
  perksOffered?: boolean;
  helpContactName?: string | null;
  helpContactEmail?: string | null;
  /** The login email, shown in the Switch Chamber window. */
  loginEmail?: string | null;
  onLogout: () => void;
  onNavigate?: () => void;
};

/** "Signed in as" summary from the access the backend granted. */
// Design role lines (ACC:L1801-1811) for the role defaults. A login with other
// saved permissions gets the generated line below.
const DESIGN_ROLE_SUMMARY: Record<string, { flags: [boolean, boolean, boolean, boolean, boolean]; summary: string }> = {
  accountsPayable: { flags: [true, false, false, true, true], summary: 'Update profile · Category banners · View and pay invoices · Register for events' },
  administrator: { flags: [true, true, true, true, false], summary: 'All features except granting access and invoices' },
};

export const getCmpMemberRoleSummary = (access: CmpMemberDashboardAccessReturn): string => {
  if (access.isPrimaryContact) return 'Full access to all dashboard features';
  const design = DESIGN_ROLE_SUMMARY[access.contactId || ''];
  const flags = [access.canManageProfile, access.canCreatePerks, access.canManagePerks, access.canManageBanners, access.canViewInvoices];
  if (design && design.flags.every((value, index) => value === Boolean(flags[index]))) return design.summary;
  const parts = [
    access.canManageProfile ? 'Update profile' : '',
    access.canCreatePerks || access.canManagePerks ? 'Create and manage perks' : '',
    access.canManageBanners ? 'Category banners' : '',
    access.canViewInvoices ? 'View and pay invoices' : '',
  ].filter(Boolean);
  return parts.join(' · ');
};

type ContactSource = {
  adminConsoleContactName?: string | null; adminConsoleLoginEmail?: string | null;
  contactName?: string | null; email?: string | null;
  customContacts?: readonly ({ name?: string | null; email?: string | null } | null)[] | null;
} | null | undefined;

/** Name of the member contact that owns the signed-in email, for the Help form. */
export const getSignedInContactName = (member: ContactSource, email?: string | null): string => {
  const target = (email || '').trim().toLowerCase();
  if (!member || !target) return '';
  const same = (value?: string | null) => (value || '').trim().toLowerCase() === target;
  const custom = (member.customContacts || []).find(contact => contact && same(contact.email) && (contact.name || '').trim());
  if (custom) return (custom.name || '').trim();
  if (same(member.adminConsoleLoginEmail) && (member.adminConsoleContactName || '').trim()) return (member.adminConsoleContactName || '').trim();
  if (same(member.email) && (member.contactName || '').trim()) return (member.contactName || '').trim();
  return '';
};

// Design icons (CMP Accelerator.dc.html sidebar). Stroke icons are 22px, stroke 1.8.
const Icon = ({ d }: { d: React.ReactNode }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);

const ProfileIcon = () => <Icon d={<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="10" r="3" /><path d="M6.2 18.3a6.5 6.5 0 0111.6 0" /></>} />;
const BannersIcon = () => <Icon d={<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 15l5-5 4 4 3-3 6 6" /><circle cx="16" cy="9" r="1.5" /></>} />;
const InvoicesIcon = () => <Icon d={<><path d="M7 3h10a1.5 1.5 0 011.5 1.5V21l-2.5-1.5L13.5 21 12 19.9 10.5 21l-2.5-1.5L5.5 21V4.5A1.5 1.5 0 017 3z" /><path d="M13.7 8.4c-.3-.6-1-1-1.7-1-1 0-1.8.6-1.8 1.3 0 .8.7 1.1 1.8 1.3 1.1.2 1.8.6 1.8 1.3 0 .8-.8 1.4-1.8 1.4-.8 0-1.5-.4-1.8-1M12 6.2v1.2M12 12.7v1.2" /></>} />;
const EventsIcon = () => <Icon d={<><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4M9 15l2 2 4-4" /></>} />;
/** Sparkles (design: Create Company Perks), 24px filled. */
const CreatePerkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 299.25 201.75" fill="currentColor" aria-hidden="true">
    <path d="M74.53 0.13C67.27 61.51 61.38 67.39 0 74.66c61.4 7.29 67.29 13.28 74.53 74.53 7.27-61.38 13.15-67.27 74.53-74.53C87.68 67.39 81.8 61.51 74.53 0.13Z" />
    <path d="M227.59 59.92c-6.88 58.1-12.45 63.67-70.56 70.55 57.76 6.86 63.33 12.53 70.56 70.55 6.87-58.1 12.45-63.68 70.55-70.55-58.1-6.88-63.68-12.45-70.55-70.55Z" />
  </svg>
);
/** Open hand with sparkles (design: Manage Company Perks): the design's PNG used as a mask so it takes the item colour. */
const ManagePerksIcon = () => {
  const mask = `url("${PerksHandIcon}") center / contain no-repeat`;
  return (
    <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24 }}>
      <span data-icon="perks-hand" style={{ display: 'block', width: 26, height: 22, background: 'currentColor', WebkitMask: mask, mask }} />
    </span>
  );
};
const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.6 2.5 3.9 5.5 3.9 9S14.6 18.5 12 21c-2.6-2.5-3.9-5.5-3.9-9S9.4 5.5 12 3z" />
  </svg>
);

// Essentials (CPA Basic) lock: CMP Essentials design (Member Dashboard (1)), with the item's design icon.
const LockedNavItem = ({ title, testId, icon }: { title: string; testId: string; icon: React.ReactNode }) => (
  <div data-testid={testId} aria-disabled="true" title="Available on CPA Standard — upgrade to create and manage perks."
    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 12, minHeight: 60, boxSizing: 'border-box',
      border: '2px solid transparent', opacity: 0.45, cursor: 'not-allowed', fontFamily: "'DM Sans', sans-serif" }}>
    <span style={{ display: 'flex', flexShrink: 0, color: GRAY }}>{icon}</span>
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 14, color: GRAY, fontWeight: 500 }}>{title}</span>
      <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.02em', color: GRAY }}>NOT OFFERED BY YOUR CHAMBER</span>
    </span>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GRAY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }} aria-hidden="true"><rect x="4" y="10.5" width="16" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 018 0v3.5" /></svg>
  </div>
);

const useStyles = makeStyles({
  // The shared invoice summary colours its own line (red/green); the design
  // shows it in the item colour (black, gold when active).
  inheritColor: { '& *': { color: 'inherit !important' } },
  logout: { '&:hover': { background: 'rgba(237,28,36,0.14) !important' } },
});

/**
 * Design nav item. Active = gold title, icon and subtitle plus a 4px gold bar
 * on the right edge; no box border, no background. Subtitles are 400: the
 * design sets 300 on the profile line, but its DM Sans load has no 300 face,
 * so it renders at 400 (this app loads a real 300, which would look lighter).
 */
const NavItem = ({ active, icon, title, sub, subColor, bar = true, onClick, testId }: {
  active: boolean; icon: React.ReactNode; title: string; sub?: React.ReactNode; subColor?: string;
  bar?: boolean; onClick: () => void; testId: string;
}) => {
  const color = active ? GOLD : BLACK;
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-current={active ? 'page' : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', position: 'relative',
        minHeight: 60, boxSizing: 'border-box', border: '2px solid transparent', background: 'transparent', boxShadow: 'none', textAlign: 'left',
        fontFamily: "'DM Sans', sans-serif", width: '100%', margin: 0,
      }}>
      {bar ? <span data-testid={`${testId}-bar`} aria-hidden="true" style={{ position: 'absolute', right: 0, top: 12, bottom: 12, width: 4,
        borderRadius: '4px 0 0 4px', background: active ? GOLD : 'transparent' }} /> : null}
      <span data-testid={`${testId}-icon`} style={{ display: 'flex', flexShrink: 0, color }}>{icon}</span>
      {sub ? (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 14, color, fontWeight: 600, lineHeight: 1.3 }}>{title}</span>
          <span style={{ fontSize: 12, fontWeight: 400, color: subColor || color }}>{sub}</span>
        </span>
      ) : <span style={{ fontSize: 14, color, fontWeight: 600, lineHeight: 1.3 }}>{title}</span>}
    </button>
  );
};

export default function CmpMemberSidebar({
  access, chamberName, memberName, memberLogo, featuredMember, sponsorLogo, sponsorLogoLink, perksOffered = true, helpContactName, helpContactEmail,
  loginEmail, onLogout, onNavigate,
}: CmpMemberSidebarProps) {
  const history = useHistory();
  const classes = useStyles();
  const [helpOpen, setHelpOpen] = useState(false);
  const { pathname } = useLocation();
  const banners = useGetActiveCategoryAdvertisingBannersForChamberMemberQuery({ skip: !access.canManageBanners, fetchPolicy: 'cache-and-network' });
  const bannersActive = (banners.data?.getActiveCategoryAdvertisingBannersForChamberMember || []).length > 0;
  const go = (route: string) => { history.push(route); onNavigate?.(); };
  const isActive = (route: string) => pathname === route || pathname.startsWith(`${route}/`);
  const initials = (memberName || '').split(/\s+/).map(word => word[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  // Design subtitle colours: green when the upgrade is active, otherwise the item colour.
  const upgradeSubColor = (on: boolean) => (on ? GREEN : undefined);
  const profileActive = isActive(RedirectUri.manageCompanyProfileChamberMemberAdmin);
  const createActive = isActive(RedirectUri.createDiscountChamberMemberAdmin);
  const bannersRouteActive = isActive(RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember);
  const invoicesActive = isActive(RedirectUri.memberInvoices);

  return (
    <aside data-testid="cmp-member-sidebar" style={{
      display: 'flex', flexDirection: 'column', padding: '24px 16px 20px', boxSizing: 'border-box', minHeight: '100%',
      background: '#FFFFFF', borderRight: `1px solid ${DIVIDER}`, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0 8px 14px', marginBottom: 14, borderBottom: `1px solid ${DIVIDER}`, flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: BLACK, textAlign: 'center', letterSpacing: '0.02em', textTransform: 'none' }}>
          {chamberName ? `${chamberName} Member Dashboard` : 'Member Dashboard'}
        </div>
        <CmpSwitchChamber chamberName={chamberName} loginEmail={loginEmail} />
        {memberLogo
          ? <img src={memberLogo} alt="Member logo" style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', marginTop: 2 }} />
          : <span aria-hidden="true" data-testid="cmp-member-initials" style={{ width: 60, height: 60, borderRadius: '50%', background: GOLD, color: '#FFFFFF', fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>{initials}</span>}
        <div style={{ fontSize: 16, fontWeight: 600, color: BLACK, textAlign: 'center' }}>{memberName}</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: BLACK, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            <span>Signed in as</span><span>{access.roleLabel || 'Company Contact'}</span>
          </span>
          <span style={{ fontSize: 12, fontWeight: 400, color: BLACK, textAlign: 'center', lineHeight: 1.4 }}>{getCmpMemberRoleSummary(access)}</span>
        </div>
      </div>

      {/* Design item "Member Dashboard User Guide" (NEW pill) is hidden for now (Moeen, Oct 15 2026: nothing designed for what it opens). */}
      <nav aria-label="Member Dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {access.canManageProfile ? (
          <NavItem testId="nav-profile" active={profileActive}
            onClick={() => go(RedirectUri.manageCompanyProfileChamberMemberAdmin)} title="Manage Company Profile" icon={<ProfileIcon />}
            sub={featuredMember ? 'Featured Member' : 'Upgrade to a Featured Member and add banners to your profile page'}
            subColor={upgradeSubColor(featuredMember)} />
        ) : null}
        {!perksOffered && access.canCreatePerks ? <LockedNavItem title="Create Company Perks" testId="nav-create-perk-locked" icon={<CreatePerkIcon />} /> : null}
        {!perksOffered && access.canManagePerks ? <LockedNavItem title="Manage Company Perks" testId="nav-perks-locked" icon={<ManagePerksIcon />} /> : null}
        {perksOffered && access.canCreatePerks ? (
          <NavItem testId="nav-create-perk" active={createActive}
            onClick={() => go(RedirectUri.createDiscountChamberMemberAdmin)} title="Create Company Perks" icon={<CreatePerkIcon />}
            sub={featuredMember ? 'Featured Member' : 'Upgrade to a Featured Member and add banners to your perks you create'}
            subColor={upgradeSubColor(featuredMember)} />
        ) : null}
        {perksOffered && access.canManagePerks ? (
          <NavItem testId="nav-perks" active={pathname === RedirectUri.manageDiscountChamberMemberAdmin}
            onClick={() => go(RedirectUri.manageDiscountChamberMemberAdmin)} title="Manage Company Perks" icon={<ManagePerksIcon />} />
        ) : null}
        {access.canManageBanners ? (
          <NavItem testId="nav-banners" active={bannersRouteActive}
            onClick={() => go(RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember)} title="Manage Category Advertising Banners"
            icon={<BannersIcon />}
            sub={bannersActive ? 'Category Advertising Banners Active' : 'Purchase Category Advertising Banner'}
            subColor={upgradeSubColor(bannersActive)} />
        ) : null}
        {access.canViewInvoices ? (
          <NavItem testId="nav-invoices" active={invoicesActive}
            onClick={() => go(RedirectUri.memberInvoices)} title="Invoices & Payments" icon={<InvoicesIcon />}
            sub={<span className={classes.inheritColor}><MemberInvoiceNavSummary /></span>} />
        ) : null}
        {access.canManageProfile ? (
          // Design: the events item has no right-edge bar when active (gold text and icon only).
          <NavItem testId="nav-events" active={isActive(RedirectUri.memberEvents)} bar={false}
            onClick={() => go(RedirectUri.memberEvents)} title="Register for Events" sub="And update attendee details" icon={<EventsIcon />} />
        ) : null}
      </nav>

      <div style={{ flex: 1, minHeight: 24 }} />
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: GRAY, margin: '0 0 10px', padding: '0 8px' }}>SPONSORED BY:</div>
      <div style={{ background: '#FFFFFF', border: `1px solid ${DIVIDER}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(0,0,0,.04)', padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 8px' }}>
        {sponsorLogo ? (
          <a href={getExternalLinkUrl(sponsorLogoLink)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}>
            <img src={sponsorLogo} alt="Sponsor logo" style={{ maxWidth: 200, maxHeight: 70, objectFit: 'contain' }} />
          </a>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: '100%' }}>
            <div style={{ width: 140, height: 48, border: '1px dashed #C4C4C8', borderRadius: 8, background: '#F7F7F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 400, color: GRAY, letterSpacing: '0.04em' }}>YOUR LOGO</span>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>Available</div>
          </div>
        )}
      </div>
      <div style={{ height: 1, background: DIVIDER, margin: '24px 8px 0' }} />
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: GRAY, margin: '20px 0 8px', padding: '0 8px' }}>GENERAL</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button type="button" onClick={() => go(RedirectUri.settings)} style={generalRow} data-testid="cmp-nav-language">
          <span style={{ display: 'flex', flexShrink: 0, color: GOLD }}><GlobeIcon /></span>
          <span style={{ fontSize: 14, fontWeight: 600, color: BLACK }}>Language Preference</span>
        </button>
        <button type="button" onClick={() => setHelpOpen(true)} style={generalRow} data-testid="cmp-nav-help">
          <img src={HelpIcon} alt="" style={{ width: 20, height: 20, flexShrink: 0 }} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: BLACK }}>Need Help?</span>
            <span style={{ fontSize: 12, fontWeight: 400, color: BLACK }}>Click to send to your chamber</span>
          </span>
        </button>
        <button type="button" onClick={onLogout} style={generalRow} className={classes.logout} data-testid="cmp-nav-logout">
          <img src={LogoutIcon} alt="" style={{ width: 20, height: 20, flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: RED }}>Logout</span>
        </button>
      </div>
      <div style={{ fontSize: 11, color: GRAY, textAlign: 'center', lineHeight: 1.5, marginTop: 20 }}>
        Copyright @ {new Date().getFullYear()},<br />One Chamber System™ Dashboard
      </div>
      {helpOpen && <CmpMemberHelpModal chamberName={chamberName || 'your chamber'} defaultName={helpContactName}
        defaultEmail={helpContactEmail} onClose={() => setHelpOpen(false)} />}
    </aside>
  );
}

const generalRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderRadius: 12, cursor: 'pointer',
  background: 'transparent', border: 'none', textAlign: 'left', fontFamily: "'DM Sans', sans-serif", width: '100%', margin: 0,
};
